import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, Loading } from "../components/Layout";
import { AlertTriangle } from "lucide-react";

// Generic, vendor-neutral labels for the spend breakdown (no AI-vendor names in UI).
const SERVICE_LABEL = {
  claude_sonnet: "AI Drafting",
  claude_haiku: "AI Classifier",
  apollo: "Apollo Research",
  kaspr: "Kaspr Enrichment",
  sendgrid_email: "Email",
  linkedin_touch: "LinkedIn Outreach",
  twilio_minute: "Voice Minutes",
  whatsapp_message: "WhatsApp Message",
};

function Stat({ label, value, sub }) {
  return (
    <div className="border border-[#DFDAD0] bg-white p-4">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">{label}</div>
      <div className="font-mono text-2xl font-medium text-[#1C2530] mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-[#1C2530]/50 mt-1">{sub}</div>}
    </div>
  );
}

export default function Spend() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/spend").then((r) => setD(r.data)); }, []);
  if (!d) return (<><PageHeader eyebrow="ADMIN" title="Spend Meter" /><div className="p-8"><Loading /></div></>);

  const pct = Math.min(100, (d.total_spend / d.budget) * 100);
  const over = pct >= 90;
  const maxSvc = Math.max(...Object.values(d.by_service), 0.01);

  return (
    <>
      <PageHeader eyebrow="ADMIN · USAGE / COST EVENT LOG" title="Spend Meter & Cost-to-Conversion" hint="Every metered action (AI drafts, enrichment lookups, calls) logged with its cost, rolled up against conversions so you can see cost per meeting and per deal." />
      <div className="p-8 space-y-6">
        <div className="border border-[#DFDAD0] bg-white p-5" data-testid="spend-meter">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">This Period · vs Budget Cap</div>
            <div className="font-mono text-sm tabular-nums text-[#1C2530]/70">${d.total_spend} / ${d.budget}</div>
          </div>
          <div className="h-8 bg-[#F5F3EE] border border-[#DFDAD0] overflow-hidden">
            <div className="h-full flex items-center justify-end px-2" style={{ width: `${pct}%`, background: over ? "#B23A2E" : "#3E8F99" }}>
              <span className="font-mono text-xs text-white tabular-nums">{pct.toFixed(0)}%</span>
            </div>
          </div>
          {over && <div className="flex items-center gap-2 mt-2 text-[#B23A2E] font-mono text-xs"><AlertTriangle className="w-3.5 h-3.5" /> BUDGET THRESHOLD BREACH</div>}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="Per Researched" value={`$${d.cost_per_researched}`} />
          <Stat label="Per Contacted" value={`$${d.cost_per_contacted}`} />
          <Stat label="Per Qualified" value={`$${d.cost_per_qualified}`} />
          <Stat label="Per Booked" value={`$${d.cost_per_booked}`} />
          <Stat label="Per Closed Deal" value={`$${d.cost_per_deal}`} sub="true cost" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-[#DFDAD0] bg-white p-5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-4">Spend by Service</div>
            <div className="space-y-2">
              {Object.entries(d.by_service).sort((a, b) => b[1] - a[1]).map(([svc, cost]) => (
                <div key={svc} className="flex items-center gap-3">
                  <div className="w-32 font-mono text-xs text-[#1C2530]/70 truncate">{SERVICE_LABEL[svc] || svc}</div>
                  <div className="flex-1 h-4 bg-[#F5F3EE] border border-[#DFDAD0]">
                    <div className="h-full bg-[#E2711D]" style={{ width: `${(cost / maxSvc) * 100}%` }} />
                  </div>
                  <div className="w-16 text-right font-mono text-xs tabular-nums">${cost.toFixed(3)}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-[#DFDAD0] bg-white p-5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-4">ROI Panel</div>
            <div className="font-mono text-6xl font-bold text-[#2F7A3E] tabular-nums">{d.roi_multiple}×</div>
            <div className="text-sm text-[#1C2530]/70 mt-2">Won value ${d.won_value.toLocaleString()} ÷ spend ${d.total_spend}</div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <Stat label="Won Deals" value={d.won_count} />
              <Stat label="Cost Events" value={d.event_count} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
